import React from 'react';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import InlineFeedback, { type InlineFeedbackProps } from '@/components/shared/InlineFeedback';

type InvoiceStatusFeedback = Pick<InlineFeedbackProps, 'variant' | 'title' | 'message'>;

interface InvoiceStatusConfirmDialogProps {
  open: boolean;
  targetStatus: 'paid' | 'discarded' | null;
  isSubmitting?: boolean;
  feedback?: InvoiceStatusFeedback | null;
  onCancel: () => void;
  onConfirm: () => void;
}

const labelForStatus = (status: 'paid' | 'discarded' | null): string => {
  if (status === 'paid') return 'mark this invoice as paid';
  if (status === 'discarded') return 'discard this invoice';
  return 'update invoice status';
};

const InvoiceStatusConfirmDialog: React.FC<InvoiceStatusConfirmDialogProps> = ({
  open,
  targetStatus,
  isSubmitting = false,
  feedback,
  onCancel,
  onConfirm,
}) => {
  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!isSubmitting) onCancel();
      }}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: '16px' } }}
    >
      <DialogTitle sx={{ fontSize: '1.1rem', fontWeight: 600, pb: 0.5 }}>Confirm status change</DialogTitle>
      <DialogContent sx={{ pt: 1, pb: 0 }}>
        <Typography sx={{ fontSize: '0.9rem', color: '#4a5565' }}>
          Are you sure you want to {labelForStatus(targetStatus)}?
        </Typography>
        {feedback ? (
          <Box sx={{ mt: 2 }}>
            <InlineFeedback
              variant={feedback.variant}
              title={feedback.title}
              message={feedback.message}
            />
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1, pt: 2 }}>
        <Button
          variant="outlined"
          onClick={onCancel}
          disabled={isSubmitting}
          sx={{ textTransform: 'none', borderRadius: '10px' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={isSubmitting}
          sx={{ textTransform: 'none', borderRadius: '10px', bgcolor: '#16a34a', '&:hover': { bgcolor: '#15803d' } }}
        >
          {isSubmitting ? <CircularProgress size={20} color="inherit" /> : 'Confirm'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoiceStatusConfirmDialog;
